import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { useMobileSigningLocale } from "@/i18n/use-mobile-signing-locale";
import type { AppUser, UserStatus } from "@/types";

const STATUS_CHIP: Record<UserStatus, string> = {
  Active: "bg-success-soft text-success",
  Invited: "bg-warning-soft text-warning",
  Suspended: "bg-danger-soft text-danger",
};

/** An invitation's own lifecycle, as the admin reads it. Unknown values fall
 * through to the neutral chip and keep the raw API label (never a guess). */
const INVITE_STATUS_CHIP: Record<string, string> = {
  Pending: "bg-warning-soft text-warning",
  Expired: "bg-danger-soft text-danger",
  Accepted: "bg-success-soft text-success",
};

/**
 * Last-resort rendering of a role: `wallet_operator` → "Wallet Operator". The
 * role catalog is the authority for what a role is called, but it is fetched
 * separately and can fail — a raw `roleName` is still far more readable
 * title-cased than left as an identifier, and an invite row carries only the
 * raw name. Used for both the row chips and the role filter's options.
 */
export function roleDisplayLabel(roleName: string): string {
  return roleName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The roles a row holds, as chips. `roleName` is a comma-separated list on the
 * wire and a person can hold more than one role, so the cell renders one chip
 * per role instead of a comma-joined string that reads as a single job title.
 * Each chip's `title` carries the raw `roleName` an API or audit record
 * names — the display name is for reading, the id is for matching.
 */
function RoleChips({ role, roleLabels }: { role: string; roleLabels?: Record<string, string> }) {
  const roles = (role || "")
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
  if (roles.length === 0) return <span className="text-[11.5px] text-muted">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r}
          title={r}
          className="inline-flex items-center rounded-full bg-primary-soft px-2 py-[3px] text-[10.5px] font-semibold leading-none whitespace-nowrap text-primary"
        >
          {roleLabels?.[r] ?? roleDisplayLabel(r)}
        </span>
      ))}
    </span>
  );
}

/**
 * Invite-only row detail. Kept beside `deliveryStatus` rather than widened into
 * `AppUser` (shared with real users): an invite has no MFA method, no groups,
 * and a lifecycle status a user record does not carry.
 */
export interface InviteListInfo {
  /** "Pending" | "Expired" | "Accepted", or the raw API value if unrecognised. */
  statusLabel: string;
  /** ISO expiry, rendered only for an expired invite so the row says when. */
  expiresAt?: string;
}

function StatusBadge({ status, invite }: { status: UserStatus; invite?: InviteListInfo }) {
  const label = invite ? invite.statusLabel || "—" : status;
  const chip = invite
    ? (INVITE_STATUS_CHIP[invite.statusLabel] ?? "bg-disabled-soft text-disabled")
    : (STATUS_CHIP[status] ?? "bg-disabled-soft text-disabled");
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-[3px] text-[10px] font-semibold leading-none whitespace-nowrap ${chip}`}>
      {label}
    </span>
  );
}

const DELIVERY_VARIANT: Record<string, "success" | "warning" | "danger" | "info" | "pending" | "neutral"> = {
  QUEUED: "pending",
  SENT: "info",
  DELIVERED: "success",
  FAILED: "danger",
  NOT_CONFIGURED: "warning",
  BOUNCED: "danger",
};

/**
 * Invite delivery status as a design-system badge with an i18n label.
 * Unknown values fall through to a neutral badge showing the raw value (never
 * a crash), and FAILED / NOT_CONFIGURED add a hint to share the invite link
 * another way — the invite token itself is never displayed.
 */
function DeliveryCell({ status }: { status?: string }) {
  const { invites } = useMobileSigningLocale();
  if (!status) {
    return <span className="text-[11.5px] text-muted">—</span>;
  }
  const labels = invites.deliveryStatus;
  const label =
    status === "QUEUED" ? labels.queued :
    status === "SENT" ? labels.sent :
    status === "FAILED" ? labels.failed :
    status === "NOT_CONFIGURED" ? labels.notConfigured :
    status === "DELIVERED" ? labels.delivered :
    status === "BOUNCED" ? labels.bounced :
    status;
  const needsHint = status === "FAILED" || status === "NOT_CONFIGURED";
  return (
    <div>
      <Badge variant={DELIVERY_VARIANT[status] ?? "neutral"} size="sm">{label}</Badge>
      {needsHint && (
        <p className="mt-1 max-w-[180px] text-[10.5px] leading-snug text-muted">{invites.deliveryShareHint}</p>
      )}
    </div>
  );
}

/** Where an invitation lapsed. The badge says "Expired"; this says when, so the
 * administrator can judge whether to invite the person again. */
function InviteExpiryNote({ expiresAt }: { expiresAt: string }) {
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return null;
  return (
    <p className="mt-1 max-w-[180px] text-[10.5px] leading-snug text-danger">
      The invite expired on {date.toLocaleDateString()}.
    </p>
  );
}

export function UserRow({
  user,
  deliveryStatus,
  invite,
  roleLabels,
}: {
  user: AppUser;
  deliveryStatus?: string;
  invite?: InviteListInfo;
  /** roleName → display name, from the role catalog. Optional: without it the
   *  cell falls back to a title-cased `roleName`, so a roles endpoint that is
   *  slow or unavailable never blanks the column. */
  roleLabels?: Record<string, string>;
}) {
  return (
    <tr className="border-b border-line last:border-b-0">
      <td className="px-4 py-[9px]">
        <Link to={`/admin/users/${user.id}`} className="text-[11.5px] font-semibold text-primary hover:underline">
          {user.name}
        </Link>
      </td>
      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">{user.email}</td>
      <td className="px-4 py-[9px] whitespace-nowrap">
        <RoleChips role={user.role} roleLabels={roleLabels} />
      </td>
      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">
        {user.groups.length > 0 ? user.groups.join(", ") : "—"}
      </td>
      {/* "Verified" answers the question the column header asks — is this
          person's second factor set up — which a bare method name (`totp`)
          does not. The method itself is not discarded: it is the cell's
          tooltip, and it is what the MFA filter still matches on. */}
      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px]">
        {user.mfa ? (
          <span className="font-semibold text-success" title={`MFA method: ${user.mfa}`}>
            Verified
          </span>
        ) : (
          <span className="text-muted" title="No MFA method on this account">
            Not verified
          </span>
        )}
      </td>
      <td className="px-4 py-[9px]">
        <StatusBadge status={user.status} invite={invite} />
        {invite?.statusLabel === "Expired" && invite.expiresAt && (
          <InviteExpiryNote expiresAt={invite.expiresAt} />
        )}
      </td>
      <td className="px-4 py-[9px] whitespace-nowrap">
        <DeliveryCell status={deliveryStatus} />
      </td>
      <td className="px-4 py-[9px] whitespace-nowrap text-[11.5px] text-ink">
        {/* An invitation's date column is when it was sent — nobody has signed
            in yet, so "Last Active" would be wrong for these rows. */}
        {invite && user.lastActive !== "—" ? `Invited ${user.lastActive}` : user.lastActive}
      </td>
    </tr>
  );
}
